import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { brandInfo } from '../data/products';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Message sent successfully!', {
        description: 'We\'ll get back to you within 24 hours.'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };

  return (
    <main className="pt-24 pb-24 bg-[#FAF8F5]" data-testid="contact-page">
      {/* Header Section */}
      <section className="py-12 md:py-20 bg-[#2D4A3E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
              Get in Touch
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mt-3 mb-4" data-testid="contact-title">
              Contact Us
            </h1>
            <p className="font-['Montserrat'] text-base text-[#A8B5AB]">
              Have a question or need assistance? Our team is here to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1 space-y-6"
            >
              <div className="bg-white border border-[#E8E4DE] p-6">
                <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">
                  Contact Information
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4" data-testid="contact-address">
                    <div className="w-10 h-10 bg-[#C67D4E]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#C67D4E]" size={18} />
                    </div>
                    <div>
                      <h3 className="font-['Montserrat'] font-semibold text-sm text-[#2D4A3E]">Address</h3>
                      <p className="font-['Montserrat'] text-sm text-[#5A6B5D] mt-1">{brandInfo.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-email">
                    <div className="w-10 h-10 bg-[#C67D4E]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#C67D4E]" size={18} />
                    </div>
                    <div>
                      <h3 className="font-['Montserrat'] font-semibold text-sm text-[#2D4A3E]">Email</h3>
                      <a 
                        href={`mailto:${brandInfo.email}`} 
                        className="font-['Montserrat'] text-sm text-[#5A6B5D] mt-1 hover:text-[#C67D4E] transition-colors"
                      >
                        {brandInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-phone">
                    <div className="w-10 h-10 bg-[#C67D4E]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#C67D4E]" size={18} />
                    </div>
                    <div>
                      <h3 className="font-['Montserrat'] font-semibold text-sm text-[#2D4A3E]">Phone</h3>
                      <a 
                        href={`tel:${brandInfo.phone}`} 
                        className="font-['Montserrat'] text-sm text-[#5A6B5D] mt-1 hover:text-[#C67D4E] transition-colors"
                      >
                        {brandInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-hours">
                    <div className="w-10 h-10 bg-[#C67D4E]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="text-[#C67D4E]" size={18} />
                    </div>
                    <div>
                      <h3 className="font-['Montserrat'] font-semibold text-sm text-[#2D4A3E]">Business Hours</h3>
                      <p className="font-['Montserrat'] text-sm text-[#5A6B5D] mt-1">{brandInfo.hours}</p>
                      <p className="font-['Montserrat'] text-xs text-[#8B7355] mt-1">Sat - Sun: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-[#E8E4DE] aspect-square" data-testid="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.11976397304603!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2s!4v1704067200000!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Bright Vista Location"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <div className="bg-white border border-[#E8E4DE] p-6 md:p-10">
                <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">
                  Send Us a Message
                </h2>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="form-success"
                  >
                    <div className="w-16 h-16 mx-auto bg-[#10B981]/10 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle className="text-[#10B981]" size={32} />
                    </div>
                    <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-2">
                      Message Sent!
                    </h3>
                    <p className="font-['Montserrat'] text-sm text-[#5A6B5D] mb-6">
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </p>
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      className="bg-[#2D4A3E] text-white hover:bg-[#1E332A] rounded-sm font-['Montserrat']"
                    >
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat']"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat']"
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat']"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          className="mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat']"
                          data-testid="input-subject"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] resize-none font-['Montserrat']"
                        data-testid="input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[#C67D4E] text-white hover:bg-[#A86840] px-8 py-6 rounded-sm text-xs uppercase tracking-wider font-semibold flex items-center gap-2 disabled:opacity-50 font-['Montserrat']"
                      data-testid="submit-contact-form"
                    >
                      {isSubmitting ? 'Sending...' : (
                        <>
                          <Send size={16} />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};
